import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import {
  PERSONIFY_REWARD_EVENT_TYPE_OPTIONS,
  PERSONIFY_REWARD_STATUS_OPTIONS,
} from "@/constants/integrations";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useIntegrations } from "@/hooks/use-integrations";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { PersonifyRewardEvent } from "@/types/integrations";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { SearchHeaderSelect } from "@/ui/table/search-header-select";
import { formatShortDate } from "@/utils";

const columns: ColumnDef<PersonifyRewardEvent>[] = [
  { id: "id", accessorKey: "id" },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => {
      return row.original.createdAt ? formatShortDate(row.original.createdAt) : "—";
    },
  },
  {
    id: "externalMemberId",
    accessorKey: "externalMemberId",
    header: (header) => {
      return <HeaderSearchInput header={header} name="External Member ID" />;
    },
    enableSorting: false,
  },
  {
    id: "userEmail",
    accessorKey: "userEmail",
    header: "User Email",
    cell: ({ row }) => {
      return row.original.userEmail ?? "—";
    },
    enableSorting: false,
  },
  {
    id: "eventType",
    accessorKey: "eventType",
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="Type"
          options={PERSONIFY_REWARD_EVENT_TYPE_OPTIONS}
        />
      );
    },
    enableSorting: false,
  },
  {
    id: "status",
    accessorKey: "status",
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="Status"
          options={PERSONIFY_REWARD_STATUS_OPTIONS}
        />
      );
    },
    enableSorting: false,
  },
  {
    id: "attempts",
    accessorKey: "attempts",
    header: "Attempts",
  },
  {
    id: "eventDate",
    accessorKey: "eventDate",
    header: "Event Date",
    cell: ({ row }) => {
      return row.original.eventDate ? formatShortDate(row.original.eventDate) : "—";
    },
  },
  {
    id: "succeededAt",
    accessorKey: "succeededAt",
    header: "Succeeded",
    cell: ({ row }) => {
      return row.original.succeededAt ? formatShortDate(row.original.succeededAt) : "—";
    },
  },
  {
    id: "errorCode",
    accessorKey: "errorCode",
    header: (header) => {
      return <HeaderSearchInput header={header} name="Error Code" />;
    },
    cell: ({ row }) => {
      return row.original.errorCode ?? "—";
    },
    enableSorting: false,
  },
];

const PERSONIFY_REWARD_FILTER_KEYS = [
  "externalMemberId",
  "eventType",
  "status",
  "errorCode",
] as const;

const PersonifyRewardsPage = () => {
  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: PERSONIFY_REWARD_FILTER_KEYS,
  });

  const { data: rewards, isLoading } = useIntegrations().getPersonifyRewardEvents({
    queryBuilder,
  });

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <PaginatedTable
        columns={columns}
        data={rewards?.data}
        isLoadingData={isLoading}
        pagination={rewards?.pagination}
        setCurrentPage={setCurrentPage}
        setSorting={setSorting}
        sorting={sortingState}
      />
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/integrations/personify/rewards/")({
  component: PersonifyRewardsPage,
  validateSearch: z.object({
    page: z.number().optional(),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
